import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";
import {
  formatNumber,
  formatNumberCommaSeparated,
  formatNumberCommaSeparatedText,
} from "../../utils/common";
import { monthSort } from "../../utils/constant";
import { generateMonthYearLabels } from "../../utils/generateYearRanges";
import { Box, Typography } from "@mui/material";

// Register the necessary elements and components in Chart.js
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend
);
const currentDate = new Date();
const currentYear = currentDate.getFullYear();

const LineChart = ({
  dataset,
  labels,
  yAxisTitle,
  customWidth,
  customHeight,
  display,
  year = currentYear,
  mt,
  marginTop,
  graphxasis,
  singlePointFromOrigin = false,
  xTickFormatter,
  xAxisBorderColor,
  yAxisBorderColor,
  xAxisBorderWidth = 2,
  yAxisBorderWidth = 2,
  legendPointSize = 8,
  tooltipDisplayColors = true,
}) => {
  // const maxValue = dataset
  //   ? Math.max(...dataset.flatMap((d) => d.data))
  //   : Math.max(65, 59, 80, 81, 56, 55);

  // const yAxisMax = maxValue * 1.1;

  const data = {
    labels: labels ? labels : generateMonthYearLabels(),
    datasets: dataset
      ? dataset
      : [
          {
            label: "", // Label for the dataset
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Data points for each label
            borderColor: "rgba(75, 192, 192, 1)", // Line color
            backgroundColor: "rgba(75, 192, 192, 0.2)", // Area under line color
            tension: 0.3, // Curvature of the line
            fill: true, // Fill the area under the line
            pointBackgroundColor: "rgba(75, 192, 192, 1)", // Color of data points
            pointBorderColor: "#fff", // Border color for data points
            pointRadius: 0, // Radius of each data point
          },
          {
            label: "",
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(255, 187, 51, 1)",
            backgroundColor: "rgba(255, 187, 51, 0.2)",
            tension: 0.3,
            fill: true,
            pointRadius: 0,
          },
        ],
  };

  const nonEmptyLabels = (data?.labels || []).filter(
    (label) => String(label ?? "").trim() !== ""
  );
  const useSingleOriginMode =
    singlePointFromOrigin &&
    nonEmptyLabels.length === 1 &&
    Array.isArray(data?.datasets) &&
    data.datasets.some((series) => Array.isArray(series?.data) && series.data.length >= 1);

  const renderData = useSingleOriginMode
    ? {
        labels: data.labels,
        datasets: (data.datasets || []).map((series) => {
          const raw = Array.isArray(series?.data) ? series.data : [];
          const endValue =
            raw.length >= 1 ? Number(raw[raw.length - 1]) || 0 : 0;
          return {
            ...series,
            parsing: false,
            tension: 0,
            data: [
              { x: 0, y: 0 },
              { x: 1, y: endValue },
            ],
          };
        }),
      }
    : data;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: display ? true : false,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          pointStyleWidth: legendPointSize,
          padding: 16,
          boxWidth: legendPointSize,
          boxHeight: legendPointSize,
          font: {
            family: "Inter, sans-serif",
            size: 12,
            weight: "500",
            lineHeight: 1.2,
          },
          maxBarThickness: 10,
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatNumberCommaSeparatedText(formatNumber(context.parsed.y));
            }
            return label;
          },
          title: function(context) {
            // In single-origin mode, x is numeric (0/1). Show semantic label instead of "1".
            if (useSingleOriginMode) {
              return data?.labels?.[data?.labels?.length - 1] || "";
            }
            // Show month/quarter label in tooltip title
            if (context.length > 0 && context[0].label) {
              return context[0].label || "";
            }
            return "";
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 10,
        displayColors: tooltipDisplayColors,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        ...(useSingleOriginMode
          ? {
              type: "linear",
              min: 0,
              // Keep single-point trend ending near chart middle.
              max: 2,
              bounds: "data",
              grace: 0,
            }
          : {}),
        title: {
          display: false,
          text: "Months",
        },
        ...(xAxisBorderColor && {
          border: {
            display: true,
            color: xAxisBorderColor,
            width: xAxisBorderWidth,
          },
        }),
        beginAtZero: false,
        // Keep first point aligned from the left edge (no category side padding)
        offset: true,
        grid: {
          display: false, // Hide the vertical grid lines
        },
        ticks: {
          ...(useSingleOriginMode
            ? {
                stepSize: 1,
              }
            : {}),
          padding:graphxasis?graphxasis: 10, // Space between axis line and labels - "Jan" spacing from corner
          autoSkip: false, // Show all labels
          callback: function (value, index) {
            if (useSingleOriginMode) {
              return Number(value) === 1
                ? data?.labels?.[data?.labels?.length - 1] || ""
                : "";
            }
            const rawLabel =
              typeof this?.getLabelForValue === "function"
                ? this.getLabelForValue(value)
                : data?.labels?.[index];
            if (typeof xTickFormatter === "function") {
              return xTickFormatter(rawLabel, index);
            }
            return rawLabel;
          },
        },
      },

      y: {
        title: {
          display: yAxisTitle ? true : false,
          text: yAxisTitle ? yAxisTitle : "", // Y-axis label
        },
        ...(yAxisBorderColor && {
          border: {
            display: true,
            color: yAxisBorderColor,
            width: yAxisBorderWidth,
          },
        }),
        beginAtZero: true, // Start y-axis from zero - both lines start from 0
        min: useSingleOriginMode ? 0 : undefined,
        grace: useSingleOriginMode ? 0 : undefined,
        // max: yAxisMax,
        ticks: {
          padding: 10, // Add space between axis and labels
          callback: function (value) {
            return formatNumberCommaSeparatedText(formatNumber(value));
          }, // Add space between axis and labels
        },
      },
    },
  };

  return (
    <div
      style={{
        width: customWidth || "100%",
        minHeight: customHeight || "18rem",
        fontWeight: 600,
        padding: 0,
        marginTop:marginTop,
      }}
    >
      {dataset?.length > 0 ? (
        <Line data={renderData} options={options} />
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center", // horizontally center
            alignItems: "center", // vertically center
            height: customHeight || "18rem", // ensure it takes up space
            width: customWidth || "100%", // optional, to match parent
            mt: mt,
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontFamily: "inter",
              color: "#d3d3d3",
              fontWeight: "bold",
            }}
          >
            No Data Found
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default LineChart;